import type { CSSProperties, ReactNode } from "react";
import { useT, type Translate } from "../i18n/index.tsx";
import { isApiError } from "../api/client.ts";
import { Button } from "./Button.tsx";
import { AlertTriangleIcon, InfoIcon, LockIcon, RefreshIcon } from "./icons.tsx";

export function EmptyState({
  icon,
  title,
  children,
  actions,
  headingLevel = 2,
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  headingLevel?: 2 | 3;
}) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  return (
    <div className="empty-state">
      {icon ? <div className="empty-state-icon">{icon}</div> : null}
      <Heading>{title}</Heading>
      {children ? <div className="stack" style={{ gap: 8 }}>{children}</div> : null}
      {actions ? <div className="empty-state-actions">{actions}</div> : null}
    </div>
  );
}

/** Honest "not available yet" state (RF-UI-16): no decorative success, no fake data. */
export function UnavailableNotice({ title, children, headingLevel = 2 }: { title?: ReactNode; children?: ReactNode; headingLevel?: 2 | 3 }) {
  const t = useT();
  return (
    <EmptyState icon={<LockIcon />} title={title ?? t("common.notAvailableYet")} headingLevel={headingLevel}>
      <p>{children ?? t("common.notAvailableYetBody")}</p>
    </EmptyState>
  );
}

export function UnavailableTag({ text }: { text?: string }) {
  const t = useT();
  return (
    <span className="unavailable">
      <LockIcon size={14} />
      {text ?? t("common.notAvailableYet")}
    </span>
  );
}

export function Banner({
  tone = "info",
  title,
  children,
  action,
  meta,
  role,
  id,
  testId,
}: {
  tone?: "info" | "warning" | "error";
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  meta?: ReactNode;
  role?: "alert" | "status";
  id?: string;
  testId?: string;
}) {
  return (
    <div className={`banner banner--${tone}`} role={role} id={id} data-testid={testId}>
      {tone === "info" ? <InfoIcon /> : <AlertTriangleIcon />}
      <div className="banner-body">
        {title ? <p className="banner-title">{title}</p> : null}
        {children ? <div>{children}</div> : null}
        {meta ? <p className="banner-meta">{meta}</p> : null}
      </div>
      {action}
    </div>
  );
}

/**
 * The message of an error in the UI language: client-side network failures (no server text) are translated; server
 * messages are shown as sent (D36 i18n audit).
 */
export function localErrorMessage(error: unknown, t: Translate): string {
  if (isApiError(error) && error.code === "network") return t(error.details?.["upload"] === true ? "error.uploadInterrupted" : "error.network");
  return error instanceof Error ? error.message : t("error.generic");
}

/** Error banner for failed API requests; shows the correlation id and a retry control. */
export function ErrorBanner({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  const t = useT();
  const message = localErrorMessage(error, t);
  const correlation = isApiError(error) ? error.correlationId : null;
  return (
    <Banner
      tone="error"
      role="alert"
      title={title ?? t("error.loadFailed")}
      meta={correlation ? t("error.correlation", { id: correlation }) : undefined}
      action={
        onRetry ? (
          <Button size="sm" icon={<RefreshIcon size={14} />} onClick={onRetry}>
            {t("common.retry")}
          </Button>
        ) : undefined
      }
    >
      {message}
    </Banner>
  );
}

export function Skeleton({ width = "100%", height = 16, style }: { width?: number | string; height?: number | string; style?: CSSProperties }) {
  return <div className="skeleton" aria-hidden="true" style={{ width, height, ...style }} />;
}

export function LoadingBlock({ label, lines = 3 }: { label: string; lines?: number }) {
  return (
    <div role="status" aria-live="polite" className="stack" style={{ padding: 16 }}>
      <span className="sr-only">{label}</span>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} height={i === 0 ? 20 : 14} width={i === 0 ? "40%" : `${90 - i * 12}%`} />
      ))}
    </div>
  );
}
